import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';

import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';

import {
  PORTAL_DESKTOP_DOCK_ITEMS,
  PORTAL_DOCK_ITEM_IDS,
  formatPortalNotificationBadgeCount,
  getPortalDockSelectedItemId,
  type PortalDockLinkItem,
} from './portalNavigation';
import { PORTAL_TAB_INTERACTION_CLASS_NAME } from './portalInteractionStyles';
import { openPortalOverlay } from './portalOverlay';

interface PortalDockLinkProps {
  item: PortalDockLinkItem;
  selectedDockItemId: string | null;
  pendingCount: number;
}

function PortalDockLink({ item, selectedDockItemId, pendingCount }: PortalDockLinkProps) {
  const Icon = item.icon;
  const isActive = item.id === selectedDockItemId;
  const showPendingSignal = item.id === PORTAL_DOCK_ITEM_IDS.NOTIFICATIONS && pendingCount > 0;
  const { t } = usePortalLocale();

  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      onClick={() => openPortalOverlay('navigation')}
      className={getDockItemClassName(isActive)}
    >
      <Icon
        aria-hidden="true"
        className={clsx(
          'h-[22px] w-[22px] shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none lg:h-[24px] lg:w-[24px]',
          isActive ? 'scale-100' : 'scale-100 group-hover:-translate-y-px group-hover:scale-105',
        )}
        strokeWidth={1.8}
      />
      <span
        className={clsx(
          'max-w-full truncate transition-transform duration-300 ease-out motion-reduce:transition-none',
          isActive && 'font-bold',
        )}
      >
        {t(item.label)}
      </span>
      {showPendingSignal && (
        <span
          className="absolute right-[16%] top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-coral px-1 text-[9px] font-bold tabular-nums text-canvas ring-2 ring-canvas-pure"
          aria-label={t('{count} notifications need attention', { count: pendingCount })}
        >
          {formatPortalNotificationBadgeCount(pendingCount)}
        </span>
      )}
    </Link>
  );
}

function getDockItemClassName(isActive: boolean): string {
  return clsx(
    PORTAL_TAB_INTERACTION_CLASS_NAME,
    'group relative z-10 flex min-h-11 min-w-0 flex-col items-center justify-center gap-1 rounded-full px-0.5 py-1 text-[11px] font-semibold sm:px-1.5',
    'active:scale-[0.96]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral focus-visible:ring-offset-2 focus-visible:ring-offset-canvas-pure',
    isActive ? 'text-ink' : 'text-slate2',
  );
}

const DOCK_SELECTION_POSITION = [
  'translate-x-0',
  'translate-x-full',
  'translate-x-[200%]',
  'translate-x-[300%]',
] as const;

interface PortalMobileNavProps {
  pendingCount: number;
  hidden?: boolean;
}

export function PortalMobileNav({ pendingCount, hidden = false }: PortalMobileNavProps) {
  const location = useLocation();
  const { t } = usePortalLocale();
  const selectedDockItemId = getPortalDockSelectedItemId(location.pathname);
  const selectedDockIndex = PORTAL_DESKTOP_DOCK_ITEMS.findIndex(
    (item) => item.id === selectedDockItemId,
  );

  return (
    <nav
      aria-label={t('Parent Portal')}
      className={clsx(
        'fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-50 mx-auto max-w-[1440px] rounded-full border border-ink/15 bg-canvas-pure shadow-floating-chrome lg:max-w-[46rem]',
        hidden && 'invisible pointer-events-none',
      )}
      aria-hidden={hidden || undefined}
      data-testid="portal-navigation-dock"
    >
      <div
        className="relative grid min-h-[68px] grid-cols-4 items-stretch rounded-full p-1.5"
        data-testid="portal-navigation-items"
      >
        <span
          aria-hidden="true"
          data-testid="portal-navigation-selection"
          className={clsx(
            'pointer-events-none absolute bottom-1 left-1.5 top-1 w-[calc((100%_-_0.75rem)/4)] rounded-full',
            'transition-[transform,opacity,background-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            selectedDockIndex >= 0
              ? [
                  'bg-wash-coral opacity-100 shadow-[0_8px_20px_rgba(255,119,102,0.12)]',
                  DOCK_SELECTION_POSITION[selectedDockIndex],
                ]
              : 'bg-transparent opacity-0',
          )}
        />
        {PORTAL_DESKTOP_DOCK_ITEMS.map((item) => (
          <PortalDockLink
            key={item.id}
            item={item}
            selectedDockItemId={selectedDockItemId}
            pendingCount={pendingCount}
          />
        ))}
      </div>
    </nav>
  );
}
